import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { getEnterprisePublicGroups, requestJoinGroup, GroupResponse } from '../logic/groupCreation/groupApi';
import { useNavigate } from 'react-router-dom';
import Modal from '../components/Modal';
import { ENTERPRISE_GROUP_JOIN_CONSENT_VERSION, buildEnterpriseGroupJoinConsentText } from '../logic/consent/enterpriseGroupJoinConsent';

const ConsultantEnterpriseGroupsPage: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [groups, setGroups] = useState<GroupResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [joiningGroupId, setJoiningGroupId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  // Consent modal state
  const [selectedGroup, setSelectedGroup] = useState<GroupResponse | null>(null);
  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSubmittingJoin, setIsSubmittingJoin] = useState(false);

  const loadGroups = async (page = 1) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const response = await getEnterprisePublicGroups(token, page, 10);

      if (response.success) {
        setGroups(response.data.groups);
        setCurrentPage(response.data.pagination.currentPage);
        setTotalPages(response.data.pagination.totalPages);
        setHasNext(response.data.pagination.hasNext);
        setHasPrev(response.data.pagination.hasPrev);
      } else {
        toast.error('Failed to load enterprise groups');
      }
    } catch (error: any) {

      toast.error(error.message || 'Failed to load enterprise groups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
  }, []);

  const handleJoinRequest = (group: GroupResponse) => {
    setSelectedGroup(group);
    setIsConsentModalOpen(true);
    setConsentChecked(false);
  };

  const handleJoinRequestSubmit = async () => {
    if (!selectedGroup || !consentChecked) return;

    try {
      setIsSubmittingJoin(true);
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      // Prepare consent payload
      const consentText = buildEnterpriseGroupJoinConsentText(
        selectedGroup.groupName,
        selectedGroup.createdBy.firstName || selectedGroup.createdBy.email || 'Enterprise'
      );
      const consentPayload = {
        accepted: true,
        textVersion: ENTERPRISE_GROUP_JOIN_CONSENT_VERSION,
        text: consentText
      };

      const response = await requestJoinGroup(token, selectedGroup.id, consentPayload);

      if (response.success) {
        toast.success('Join request submitted successfully!');
        // Reload groups to update join status
        loadGroups(currentPage);
      } else {
        toast.error(response.message || 'Failed to submit join request');
      }
    } catch (error: any) {

      toast.error(error.message || 'Failed to submit join request');
    } finally {
      setIsSubmittingJoin(false);
      setIsConsentModalOpen(false);
      setSelectedGroup(null);
      setConsentChecked(false);
    }
  };

  const getJoinButtonText = (group: GroupResponse) => {
    if (joiningGroupId === group.id) {
      return 'Requesting...';
    }

    switch (group.joinStatus) {
      case 'pending':
        return 'Request Pending';
      case 'approved':
        return 'Already Member';
      case 'rejected':
        return 'Request Rejected';
      default:
        return 'Request to Join';
    }
  };

  const isJoinButtonDisabled = (group: GroupResponse) => {
    return joiningGroupId === group.id ||
           group.joinStatus === 'pending' ||
           group.joinStatus === 'approved';
  };

  if (loading) {
    return (
      <div className={`consultant-enterprise-groups ${isDarkMode ? 'dark' : ''}`}>
        <div className="consultant-enterprise-groups__container">
          <div className="consultant-enterprise-groups__loading">
            <div className="consultant-enterprise-groups__spinner">
              <div className="consultant-enterprise-groups__spinner-circle"></div>
            </div>
            <p>Loading enterprise groups...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`consultant-enterprise-groups ${isDarkMode ? 'dark' : ''}`}>
      <div className="consultant-enterprise-groups__container">
        <header className="consultant-enterprise-groups__header">
          <div className="consultant-enterprise-groups__header-content">
            <h1 className="consultant-enterprise-groups__title">Enterprise Groups</h1>
            <p className="consultant-enterprise-groups__subtitle">
              Discover and join enterprise groups to collaborate with organizations.
            </p>
          </div>
        </header>

        <main className="consultant-enterprise-groups__main">
          {groups.length === 0 ? (
            <div className="consultant-enterprise-groups__empty">
              <div className="consultant-enterprise-groups__empty-icon">
                <span className="material-symbols-outlined">business</span>
              </div>
              <h3 className="consultant-enterprise-groups__empty-title">No Enterprise Groups Available</h3>
              <p className="consultant-enterprise-groups__empty-description">
                There are currently no enterprise groups available for you to join.
                Enterprise groups are created by organizations and allow consultants to collaborate.
              </p>
            </div>
          ) : (
            <>
              <div className="consultant-enterprise-groups__grid">
                {groups.map((group) => (
                  <div key={group.id} className="consultant-enterprise-groups__card">
                    <div className="consultant-enterprise-groups__card-header">
                      <div className="consultant-enterprise-groups__card-info">
                        <h3 className="consultant-enterprise-groups__card-title">{group.groupName}</h3>
                        <div className="consultant-enterprise-groups__card-meta">
                          <span className="consultant-enterprise-groups__card-creator">
                            by {group.createdBy.firstName || group.createdBy.email}
                          </span>
                          <span className="consultant-enterprise-groups__card-type">Enterprise Group</span>
                        </div>
                      </div>
                      <div className="consultant-enterprise-groups__card-icon">
                        <span className="material-symbols-outlined">business</span>
                      </div>
                    </div>

                    <div className="consultant-enterprise-groups__card-content">
                      <p className="consultant-enterprise-groups__card-description">
                        {group.description}
                      </p>

                      {group.tags && group.tags.length > 0 && (
                        <div className="consultant-enterprise-groups__card-tags">
                          {group.tags.slice(0, 3).map((tag, index) => (
                            <span key={index} className="consultant-enterprise-groups__card-tag">
                              {tag}
                            </span>
                          ))}
                          {group.tags.length > 3 && (
                            <span className="consultant-enterprise-groups__card-tag-more">
                              +{group.tags.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="consultant-enterprise-groups__card-footer">
                      <div className="consultant-enterprise-groups__card-stats">
                        <span className="consultant-enterprise-groups__card-stat">
                          <span className="material-symbols-outlined">group</span>
                          {group.approvedMembers || 0} members
                        </span>
                        <span className="consultant-enterprise-groups__card-stat">
                          <span className="material-symbols-outlined">schedule</span>
                          {new Date(group.createdAt).toLocaleDateString()}
                        </span>
                      </div>

                      <button
                        className={`consultant-enterprise-groups__join-btn ${
                          group.joinStatus === 'approved' ? 'consultant-enterprise-groups__join-btn--approved' :
                          group.joinStatus === 'pending' ? 'consultant-enterprise-groups__join-btn--pending' :
                          'consultant-enterprise-groups__join-btn--default'
                        }`}
                        onClick={() => handleJoinRequest(group)}
                        disabled={isJoinButtonDisabled(group)}
                      >
                        {joiningGroupId === group.id ? (
                          <span className="material-symbols-outlined consultant-enterprise-groups__spinner-icon">refresh</span>
                        ) : (
                          <span className="material-symbols-outlined">
                            {group.joinStatus === 'approved' ? 'check_circle' :
                             group.joinStatus === 'pending' ? 'schedule' : 'person_add'}
                          </span>
                        )}
                        {getJoinButtonText(group)}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {(hasPrev || hasNext) && (
                <div className="consultant-enterprise-groups__pagination">
                  <button
                    className="consultant-enterprise-groups__pagination-btn"
                    onClick={() => loadGroups(currentPage - 1)}
                    disabled={!hasPrev}
                  >
                    <span className="material-symbols-outlined">chevron_left</span>
                    Previous
                  </button>

                  <span className="consultant-enterprise-groups__pagination-info">
                    Page {currentPage} of {totalPages}
                  </span>

                  <button
                    className="consultant-enterprise-groups__pagination-btn"
                    onClick={() => loadGroups(currentPage + 1)}
                    disabled={!hasNext}
                  >
                    Next
                    <span className="material-symbols-outlined">chevron_right</span>
                  </button>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* Consent Modal */}
      <Modal
        isOpen={isConsentModalOpen}
        title="Consent to Share Aggregated Data"
        onClose={() => {
          setIsConsentModalOpen(false);
          setSelectedGroup(null);
          setConsentChecked(false);
        }}
        actionLabel="I Agree & Request to Join"
        onAction={handleJoinRequestSubmit}
        actionDisabled={!consentChecked || isSubmittingJoin}
      >
        {selectedGroup && (
          <div className="join-group-consent-modal">
            <div className="join-group-consent-modal__group-info">
              <h3 className="join-group-consent-modal__group-name">{selectedGroup.groupName}</h3>
              <p className="join-group-consent-modal__consultant">
                Enterprise: {selectedGroup.createdBy.firstName || selectedGroup.createdBy.email || 'Enterprise Organization'}
              </p>
            </div>

            <div className="join-group-consent-modal__consent-text">
              <p>
                By joining <strong>"{selectedGroup.groupName}"</strong>, you authorize this Enterprise to access:
              </p>
              <ul>
                <li>Aggregated and de-identified reports from users in your consultant groups</li>
                <li>Statistical summaries and charts (averages, distributions, trends)</li>
                <li>No individual-level reports or personally identifiable information</li>
              </ul>
              <p>
                You confirm you have the authority to enable this aggregated reporting.
              </p>
            </div>

            <div className="join-group-consent-modal__checkbox-container">
              <label className="join-group-consent-modal__checkbox-label">
                <input
                  type="checkbox"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                  className="join-group-consent-modal__checkbox"
                />
                <span className="join-group-consent-modal__checkbox-text">
                  I understand and consent to share aggregated data with this Enterprise.
                </span>
              </label>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ConsultantEnterpriseGroupsPage;
